import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static outlets = ["company-admin--customer-search--index"]
  static targets = [
    "selectedCustomersLoadingIndicator",
    "selectedCustomerRecipientsFrame",
    "submitButton",
  ]

  async fetchSelectedCustomerRecipients() {
    if (!this.hasCompanyAdminCustomerSearchIndexOutlet) return

    const selectedCustomerIds = new Set()
    this.companyAdminCustomerSearchIndexOutlet.selectedCustomerIdsValue.forEach((customerId) => {
      selectedCustomerIds.add(customerId)
    })

    await this.postRecipients([...selectedCustomerIds])
  }

  async removeCustomerRecipient(event) {
    const customerIdToRemove = event.params.customerId
    const customerIds = this.customerIdsFromDom().filter(
      (customerId) => customerId !== customerIdToRemove
    )

    await this.postRecipients(customerIds)
  }

  // Refresh only the estimate (recipients unchanged) after the message, mode, or lease selection changes.
  // A newer change cancels the pending refresh so a slow, stale response can't overwrite a fresh one.
  async refreshEstimate() {
    this.estimateRequest?.abort()
    this.estimateRequest = new AbortController()

    const formData = new FormData(this.element)
    formData.append("estimate_only", "1")

    await this.post(formData, this.estimateRequest.signal)
  }

  async postRecipients(customerIds) {
    // The recipients response carries its own estimate.
    this.estimateRequest?.abort()

    const formData = new FormData()

    customerIds.forEach((customerId) => {
      formData.append("customer_ids[]", customerId)
    })

    // Keep unchecked leases unchecked; leases of newly added customers start checked.
    this.element
      .querySelectorAll('input[name="lease_ids[]"][type="checkbox"]:not(:checked)')
      .forEach((field) => {
        formData.append("excluded_lease_ids[]", field.value)
      })

    const deliveryBasisField = this.element.querySelector('input[name="delivery_basis"]:checked')
    const kindField = this.element.querySelector('input[name="kind"]')
    const bodyField = this.element.querySelector('textarea[name="template_body"]')
    const templateField = this.element.querySelector('select[name="custom_email_template_id"]')
    if (deliveryBasisField) formData.append("delivery_basis", deliveryBasisField.value)
    if (kindField) formData.append("kind", kindField.value)
    if (bodyField) formData.append("template_body", bodyField.value)
    if (templateField) formData.append("custom_email_template_id", templateField.value)

    this.toggleSelectedCustomersLoading(true)

    try {
      await this.post(formData)
    } finally {
      this.toggleSelectedCustomersLoading(false)
    }
  }

  async post(formData, signal) {
    if (!this.hasSelectedCustomerRecipientsFrameTarget) return

    const csrfToken = document.querySelector('meta[name="csrf-token"]').content

    try {
      const response = await fetch(this.selectedCustomerRecipientsFrameTarget.dataset.src, {
        body: formData,
        headers: {
          "X-CSRF-Token": csrfToken,
          Accept: "text/vnd.turbo-stream.html",
        },
        method: "POST",
        signal,
      })

      if (response.ok) {
        const html = await response.text()
        Turbo.renderStreamMessage(html)
      }
    } catch (error) {
      if (error.name !== "AbortError") throw error
    }
  }

  customerIdsFromDom() {
    return [...this.element.querySelectorAll('input[name="customer_ids[]"]')].map(
      (field) => field.value
    )
  }

  syncSelectedCustomersToOutlet() {
    if (!this.hasCompanyAdminCustomerSearchIndexOutlet) return

    this.companyAdminCustomerSearchIndexOutlet.selectedCustomerIdsValue = this.customerIdsFromDom()
  }

  toggleSelectedCustomersLoading(on) {
    if (
      !this.hasSelectedCustomersLoadingIndicatorTarget ||
      !this.hasSelectedCustomerRecipientsFrameTarget ||
      !this.hasSubmitButtonTarget
    ) {
      return
    }

    this.selectedCustomersLoadingIndicatorTarget.classList.toggle("hidden", !on)
    this.selectedCustomerRecipientsFrameTarget.classList.toggle("hidden", on)
    this.submitButtonTarget.toggleAttribute("disabled", on)
  }
}
